/**
 * Modal de paramétrage de la trésorerie
 * Permet de définir le solde initial (manuel ou calculé)
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings, Info, Wallet, Receipt, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ParametresTresorerie } from '@/types/tresorerie';
import { ChargesFixesSection } from './ChargesFixesSection';

interface TresorerieSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  params: ParametresTresorerie | null;
  onUpdate: (montant: number | null) => Promise<void>;
  soldeCalcule: number | null;
  entrepriseId: string | null;
  fromOnboarding?: boolean;
  onRequestChargesFixes?: () => void;
}

type TabType = 'solde' | 'charges';

/**
 * Formate un montant en euros
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Composant modal paramètres trésorerie - Style Centre de Pilotage
 */
export function TresorerieSettings({
  isOpen,
  onClose,
  params,
  onUpdate,
  soldeCalcule,
  entrepriseId,
  fromOnboarding = false,
  onRequestChargesFixes,
}: TresorerieSettingsProps) {
  const [useManuel, setUseManuel] = useState(params?.utiliser_solde_manuel ?? false);
  const [montant, setMontant] = useState<string>(
    params?.solde_initial_manuel?.toString() ?? ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('solde');
  const [onboardingStep, setOnboardingStep] = useState<'solde' | 'propose-charges'>('solde');

  // Mettre à jour les états quand les params changent (sauf en mode onboarding)
  useEffect(() => {
    if (params && !fromOnboarding) {
      setUseManuel(params.utiliser_solde_manuel);
      setMontant(params.solde_initial_manuel?.toString() ?? '');
    }
  }, [params, fromOnboarding]);

  // Quand on ouvre depuis l'overlay onboarding, activer le switch manuel par défaut
  useEffect(() => {
    if (isOpen && fromOnboarding) {
      setUseManuel(true);
      setOnboardingStep('solde');
      setActiveTab('solde');
    }
    if (!isOpen) {
      setOnboardingStep('solde');
    }
  }, [isOpen, fromOnboarding]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (useManuel && montant) {
        await onUpdate(parseFloat(montant));
      } else {
        await onUpdate(null);
      }
      // Si onboarding et solde manuel saisi, proposer les charges fixes
      if (fromOnboarding && useManuel && montant) {
        setOnboardingStep('propose-charges');
      } else {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleMontantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Permettre uniquement les nombres et le signe négatif
    const value = e.target.value.replace(/[^0-9.-]/g, '');
    setMontant(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0 bg-[#0F0F14]/98 backdrop-blur-xl border-white/[0.08] overflow-hidden">
        {/* ═══ HEADER ═══ */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.05]">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-white">Paramètres de trésorerie</h2>
              <p className="text-sm text-white/50">Configuration de vos finances</p>
            </div>
          </div>
        </div>

        {/* ═══ ÉCRAN PROPOSITION CHARGES FIXES (onboarding) ═══ */}
        {onboardingStep === 'propose-charges' ? (
          <div className="px-6 py-10 text-center space-y-6">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/20">
              <CheckCircle2 className="h-7 w-7 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Solde enregistré avec succès !
              </h3>
              <p className="text-white/60 text-sm leading-relaxed max-w-sm mx-auto">
                Pour un prévisionnel encore plus précis, définissez vos charges fixes mensuelles
                (loyer, assurance, leasing, expert-comptable...).
                Elles seront automatiquement intégrées dans vos prévisions de trésorerie.
              </p>
            </div>
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <Button
                onClick={() => onRequestChargesFixes?.()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Receipt className="h-4 w-4 mr-2" />
                Définir mes charges fixes
              </Button>
              <Button
                variant="ghost"
                onClick={onClose}
                className="text-white/50 hover:text-white/70"
              >
                Plus tard
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* ═══ ONGLETS ═══ */}
            <div className="px-6 py-3 border-b border-white/[0.03] flex items-center gap-2">
              <button
                onClick={() => setActiveTab('solde')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all font-medium',
                  activeTab === 'solde'
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'text-white/50 hover:bg-white/[0.05] hover:text-white/70'
                )}
              >
                <Wallet className="h-4 w-4" />
                Solde
              </button>
              <button
                onClick={() => setActiveTab('charges')}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all font-medium',
                  activeTab === 'charges'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-white/50 hover:bg-white/[0.05] hover:text-white/70'
                )}
              >
                <Receipt className="h-4 w-4" />
                Charges fixes
              </button>
            </div>

            {/* ═══ CONTENU ═══ */}
            <ScrollArea className="h-[400px]">
              <div className="p-6">
                {/* Onglet Solde */}
                {activeTab === 'solde' && (
                  <div className="space-y-5">
                    {/* Info solde calculé */}
                    {soldeCalcule !== null && (
                      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <Info className="h-5 w-5 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-sm text-white/60 mb-1">
                              Solde calculé automatiquement
                            </p>
                            <p className="text-2xl font-bold text-white">{formatCurrency(soldeCalcule)}</p>
                            <p className="text-xs text-white/40 mt-1">
                              Basé sur les paiements encaissés moins les frais engagés
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Switch utiliser solde manuel */}
                    <div className="flex items-center justify-between p-4 rounded-xl border border-white/[0.08] bg-white/[0.02]">
                      <div className="space-y-1">
                        <Label htmlFor="use-manuel" className="text-base font-medium text-white">
                          Définir un solde manuel
                        </Label>
                        <p className="text-sm text-white/50">
                          Remplace le solde calculé par votre solde bancaire réel
                        </p>
                      </div>
                      <Switch
                        id="use-manuel"
                        checked={useManuel}
                        onCheckedChange={setUseManuel}
                      />
                    </div>

                    {/* Input montant manuel */}
                    {useManuel && (
                      <div className="space-y-3 p-4 rounded-xl border border-white/[0.08] bg-white/[0.02]">
                        <Label htmlFor="montant-manuel" className="text-sm font-medium text-white">
                          Solde de trésorerie actuel (€)
                        </Label>
                        <Input
                          id="montant-manuel"
                          type="text"
                          inputMode="numeric"
                          value={montant}
                          onChange={handleMontantChange}
                          placeholder="Ex: 45000"
                          className="text-lg h-12 bg-white/[0.05] border-white/[0.1] text-white placeholder:text-white/30"
                        />
                        <p className="text-xs text-white/40">
                          Saisissez votre solde bancaire actuel. Les valeurs négatives sont autorisées (découvert).
                        </p>
                      </div>
                    )}

                    {/* Avertissement si switch désactivé */}
                    {!useManuel && (
                      <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-4">
                        <div className="flex items-start gap-3">
                          <div className="h-8 w-8 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="h-4 w-4 text-yellow-400" />
                          </div>
                          <p className="text-sm text-yellow-200/80">
                            <strong className="text-yellow-400">Note :</strong> Le solde calculé ne prend en compte que les mouvements enregistrés dans B8ild. Pour une prévision plus précise, utilisez votre solde bancaire réel.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Onglet Charges Fixes */}
                {activeTab === 'charges' && (
                  <ChargesFixesSection entrepriseId={entrepriseId} />
                )}
              </div>
            </ScrollArea>

            {/* ═══ FOOTER ═══ */}
            <DialogFooter className="px-6 py-4 border-t border-white/[0.05] bg-white/[0.02]">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
                className="border-white/[0.1] text-white/70 hover:bg-white/[0.05] hover:text-white"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {isSaving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
